import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  listOperations,
  retryOperation,
  deleteOperation,
  streamOperations,
  type Operation,
} from "../lib/api";
import { useToast } from "../components/Toast";
import { Badge, Button, Progress, ProgressTrack, ProgressIndicator } from "raft-ui";

const STATUS_VARIANTS: Record<Operation["status"], "muted" | "information" | "success" | "danger"> = {
  pending: "muted",
  in_progress: "information",
  success: "success",
  failed: "danger",
  cancelled: "muted",
};

export function Operations({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const retryToastRef = useRef<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["operations", appId],
    queryFn: () => listOperations(appId, 50),
    refetchInterval: 5000, // polling fallback in case SSE doesn't connect
  });
  const [live, setLive] = useState<Set<string>>(new Set());

  // SSE stream — when new ops come in, invalidate the cache so they show.
  useEffect(() => {
    const es = streamOperations(
      appId,
      (op) => {
        setLive((prev) => new Set([...prev, op.id]));
        qc.invalidateQueries({ queryKey: ["operations", appId] });
      },
      () => {
        // ignore — polling fallback will keep us synced
      },
    );
    return () => es.close();
  }, [appId, qc]);

  const retry = useMutation({
    mutationFn: (opId: string) => retryOperation(appId, opId),
    onMutate: () => {
      retryToastRef.current = toast.show({
        kind: "loading",
        title: "Retrying operation...",
        ttlMs: 0,
      });
    },
    onSuccess: (op) => {
      const patch = {
        kind: "success",
        title: `Retry queued (attempt #${op.retry_count + 1})`,
      } as const;
      if (retryToastRef.current !== null) toast.update(retryToastRef.current, patch);
      else toast.show(patch);
      retryToastRef.current = null;
      qc.invalidateQueries({ queryKey: ["operations", appId] });
    },
    onError: (e) => {
      const patch = {
        kind: "error",
        title: "Retry failed",
        description: (e as Error).message,
      } as const;
      if (retryToastRef.current !== null) toast.update(retryToastRef.current, patch);
      else toast.show(patch);
      retryToastRef.current = null;
    },
  });

  const remove = useMutation({
    mutationFn: (opId: string) => deleteOperation(appId, opId),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Operation log removed" });
      qc.invalidateQueries({ queryKey: ["operations", appId] });
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Delete failed",
        description: (e as Error).message,
      }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Operations</h2>
        <span className="text-xs text-foreground-muted">
          {live.size > 0 ? "live (SSE)" : "polling (5s)"} · {data?.operations.length ?? 0} recent
        </span>
      </div>

      {isLoading && <p className="text-foreground-muted text-sm">Loading…</p>}
      {error && (
        <p className="text-danger text-sm">Failed: {(error as Error).message}</p>
      )}

      <div className="space-y-6">
        {data?.operations.length === 0 && (
          <p className="text-foreground-muted text-sm">No operations yet. Upload an APK to start.</p>
        )}
        {data?.operations.map((op) => (
          <OperationRow
            key={op.id}
            op={op}
            isLive={live.has(op.id)}
            onRetry={() => retry.mutate(op.id)}
            onDelete={() => remove.mutate(op.id)}
            busy={retry.isPending || remove.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function OperationRow({
  op,
  isLive,
  onRetry,
  onDelete,
  busy,
}: {
  op: Operation;
  isLive: boolean;
  onRetry: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  let inputPayload: any = {};
  let outputPayload: any = {};
  try {
    inputPayload = JSON.parse(op.input);
  } catch {
    // ignore
  }
  try {
    outputPayload = JSON.parse(op.output);
  } catch {
    // ignore
  }

  return (
    <section className="border-t border-line-hairline pt-5">
      <div className="flex items-center gap-3">
        <Badge variant={STATUS_VARIANTS[op.status]}>{op.status}</Badge>
        <span className="font-mono text-sm">{op.kind}</span>
        <span className="text-xs text-foreground-muted">
          {timeAgo(op.created_at)}
        </span>
        {op.retry_count > 0 && (
          <span className="text-xs text-foreground-hint">retry #{op.retry_count + 1}</span>
        )}
        <div className="flex-1" />
        {op.status === "failed" && (
          <Button
            onClick={onRetry}
            disabled={busy}
            variant="outline"
            className="text-xs"
          >
            Retry
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setExpanded(!expanded)}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? "▲" : "▼"}
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          className="hover:text-danger"
          onClick={onDelete}
          disabled={busy}
          aria-label="Delete"
        >
          ×
        </Button>
      </div>

      {op.status === "in_progress" && (
        <Progress value={Math.round(op.progress * 100)} className="mt-2 gap-0">
          <ProgressTrack className="h-1 bg-layer-inset rounded-sm overflow-hidden">
            <ProgressIndicator className="bg-info transition-all" />
          </ProgressTrack>
        </Progress>
      )}

      {op.error && (
        <div className="mt-2 text-xs text-danger-strong bg-danger-soft p-2 rounded-sm">
          {op.error}
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-2 text-xs">
          <details>
            <summary className="cursor-pointer text-foreground-muted font-medium">
              Input
            </summary>
            <pre className="mt-1 bg-layer-inset p-2 rounded-sm overflow-x-auto">
              {JSON.stringify(inputPayload, null, 2)}
            </pre>
          </details>
          {op.output !== "{}" && (
            <details>
              <summary className="cursor-pointer text-foreground-muted font-medium">
                Output
              </summary>
              <pre className="mt-1 bg-layer-inset p-2 rounded-sm overflow-x-auto">
                {JSON.stringify(outputPayload, null, 2)}
              </pre>
            </details>
          )}
          <div className="text-foreground-hint font-mono">id: {op.id}</div>
        </div>
      )}
      </section>
  );
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}
